import React from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const { t } = useTranslation('common');
  const { lang = 'en' } = useParams();

  // Helper to get language-prefixed path
  const getLocalizedPath = (path) => `/${lang}${path}`;

  return (
    <footer className="bg-text-primary text-white py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-12 mb-12">
          {/* Column 1 - Brand */}
          <div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <h3 className="text-2xl font-bold mb-4">Apptek</h3>
              <p className="text-white/70 mb-6 leading-relaxed">
                {t('footer.tagline')}
              </p>
              <div className="flex gap-4">
                <a
                  href="https://linkedin.com/company/apptekdk"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center hover:bg-secondary-accent transition-colors duration-200"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" />
                  </svg>
                </a>
                <a
                  href="https://twitter.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center hover:bg-secondary-accent transition-colors duration-200"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M23 3a10.9 10.9 0 01-3.14 1.53 4.48 4.48 0 00-7.86 3v1A10.66 10.66 0 013 4s-4 9 5 13a11.64 11.64 0 01-7 2c9 5 20 0 20-11.5a4.5 4.5 0 00-.08-.83A7.72 7.72 0 0023 3z" />
                  </svg>
                </a>
              </div>
            </motion.div>
          </div>

          {/* Column 2 - Services */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <h4 className="font-bold text-lg mb-4">{t('footer.services')}</h4>
            <ul className="space-y-2">
              <li>
                <a href={getLocalizedPath('/services')} className="text-white/70 hover:text-secondary-accent transition-colors duration-200">
                  {t('footer.customSoftware')}
                </a>
              </li>
              <li>
                <a href={getLocalizedPath('/services')} className="text-white/70 hover:text-secondary-accent transition-colors duration-200">
                  {t('footer.mobileApps')}
                </a>
              </li>
              <li>
                <a href={getLocalizedPath('/services')} className="text-white/70 hover:text-secondary-accent transition-colors duration-200">
                  {t('footer.webSolutions')}
                </a>
              </li>
              <li>
                <a href={getLocalizedPath('/services')} className="text-white/70 hover:text-secondary-accent transition-colors duration-200">
                  {t('footer.cloudInfrastructure')}
                </a>
              </li>
              <li>
                <a href={getLocalizedPath('/services')} className="text-white/70 hover:text-secondary-accent transition-colors duration-200">
                  {t('footer.aiIntegration')}
                </a>
              </li>
            </ul>
          </motion.div>

          {/* Column 3 - Company */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <h4 className="font-bold text-lg mb-4">{t('footer.company')}</h4>
            <ul className="space-y-2">
              <li>
                <a href={getLocalizedPath('/about-us')} className="text-white/70 hover:text-secondary-accent transition-colors duration-200">
                  {t('footer.aboutUs')}
                </a>
              </li>
              <li>
                <a href={getLocalizedPath('/cases')} className="text-white/70 hover:text-secondary-accent transition-colors duration-200">
                  {t('footer.caseStudies')}
                </a>
              </li>
              <li>
                <a href={getLocalizedPath('/blogs')} className="text-white/70 hover:text-secondary-accent transition-colors duration-200">
                  {t('footer.blog')}
                </a>
              </li>
              <li>
                <a href="#contact" className="text-white/70 hover:text-secondary-accent transition-colors duration-200">
                  {t('footer.contact')}
                </a>
              </li>
            </ul>
          </motion.div>
        </div>

        {/* Bottom Bar */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.4 }}
          className="border-t border-white/10 pt-8"
        >
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-white/50 text-sm">
              {t('footer.copyright', { year: currentYear })}
            </p>
            <div className="flex gap-6 text-sm">
              <a href="#privacy" className="text-white/50 hover:text-secondary-accent transition-colors duration-200">
                {t('footer.privacyPolicy')}
              </a>
              <a href="#terms" className="text-white/50 hover:text-secondary-accent transition-colors duration-200">
                {t('footer.termsOfService')}
              </a>
              <a href="#cookies" className="text-white/50 hover:text-secondary-accent transition-colors duration-200">
                {t('footer.cookiePolicy')}
              </a>
            </div>
          </div>
        </motion.div>
      </div>
    </footer>
  );
};

export default Footer;
